<template>
    <div class="popper" @keyup.esc="closePopper()" :class="styles">
        <slot name="trigger"></slot>
        <slot name="content"></slot>
    </div>
</template>

<script>
    import shortid from 'shortid';

    const directions = {
        horizontal: {
            bottomStart: [1, 0, 0, 0],
            bottomEnd: [1, 0, 1, -1],
            topStart: [0, -1, 0, 0],
            topEnd: [0, -1, 1, -1],
        },
        vertical: {
            bottomStart: [1, -1, 0, -1],
            bottomEnd: [1, -1, 1, 0],
            topStart: [0, 0, 0, -1],
            topEnd: [0, 0, 1, 0],
        },
    };

    export default {
        name: 'v-popper',
        props: {
            on: {
                type: String,
                default: 'click',
                validator: event => ['click', 'hover'].includes(event),
            },
            autoHide: {
                type: Boolean,
                default: true,
            },
            fluid: {
                type: Boolean,
                default: false,
            },
            direction: {
                type: String,
                default: 'horizontal',
                validator: direction => ['horizontal', 'vertical'].includes(direction),
            },
            position: {
                type: String,
                default: 'bottomStart',
                validator: position => ['bottomStart', 'bottomEnd', 'topStart', 'topEnd'].includes(position),
            },
            offset: {
                type: Number,
                default: 4, // px
            },
            /**
             * Set to true to have the popper
             * automatically open as soon as it is mounted
             * into the parent component.
             */
            openOnMount: {
                type: Boolean,
                default: false,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                instance: null,
                hideTimer: null,
                hideDelayMs: 200,
                hoverLeave: null,
                hoverTriggerEnter: null,
                hoverPopupEnter: null,
                clickToggleState: null,
                clickDocumentCheck: null,
                instanceId: shortid.generate(),
                isPopperVisible: false,
            };
        },
        computed: {
            triggerEl() {
                const trigger = this.$slots.trigger[0];
                const element = trigger.elm.querySelector(`[data-popper-trigger='trigger']`);
                if (!element) {
                    throw new Error('Directive v-popper-trigger must be present in an element inside trigger slot');
                }
                return element;
            },
            contentEl() {
                const content = this.$slots.content[0];
                return content.elm;
            },
            isHoverTrigger() {
                return this.on === 'hover';
            },
            styles() {
                const style = {};
                if (this.fluid) {
                    style['popper-fluid'] = true;
                }
                return style;
            },
        },
        methods: {
            setPopperVisible(visible) {
                if (this.disabled && visible) {
                    // disable popper
                    return;
                }
                if (visible) {
                    this.positionContent();
                    this.contentEl.classList.add('is-visible');
                } else {
                    this.contentEl.classList.remove('is-visible');
                }
                // trigger toggle events to synchronize
                // UI styles in parent components
                this.$emit('toggle', visible !== this.isPopperVisible);
                this.$emit(visible ? 'toggle-on' : 'toggle-off');
                this.isPopperVisible = visible;
            },
            mountPopup() {
                this.identifyComponent();
                if (this.isHoverTrigger) {
                    this.setupHover();
                } else {
                    this.setupClick();
                    this.registerAutoHide();
                }
                this.$nextTick(() => {
                    this.positionContent();
                    if (this.openOnMount) {
                        this.openPopper();
                    }
                });
            },
            identifyComponent() {
                // identify unique instance of popper
                // to allow the same menu to remain open when clicked,
                // but close all similar poppers that are actively open
                this.triggerEl.setAttribute('data-popper-id', this.instanceId);
            },
            calculateContentPosition() {
                const triggerRect = this.triggerEl.getBoundingClientRect();
                const contentRect = this.contentEl.getBoundingClientRect();
                const flags = directions[this.direction][this.position];
                let contentTop = (contentRect.height * flags[1]) + (triggerRect.height * flags[0]);
                let contentLeft = (contentRect.width * flags[3]) + (triggerRect.width * flags[2]);
                if (this.direction === 'horizontal') {
                    contentTop += this.offset;
                } else {
                    contentLeft += this.offset;
                }
                return {top: contentTop, left: contentLeft};
            },
            positionContent() {
                const contentPosition = this.calculateContentPosition();
                this.contentEl.style.top = `${contentPosition.top}px`;
                this.contentEl.style.left = `${contentPosition.left}px`;
            },
            setupHover() {
                this.hoverLeave = () => {
                    clearTimeout(this.hideTimer);
                    this.hideTimer = setTimeout(() => {
                        this.closePopper();
                    }, this.hideDelayMs);
                };
                this.hoverTriggerEnter = () => {
                    clearTimeout(this.hideTimer);
                    this.openPopper();
                };
                this.hoverPopupEnter = () => {
                    clearTimeout(this.hideTimer);
                };
                // set listeners on the trigger element
                this.triggerEl.addEventListener('mouseenter', this.hoverTriggerEnter, false);
                this.triggerEl.addEventListener('mouseleave', this.hoverLeave, false);
                // set listeners on the popup element
                this.contentEl.addEventListener('mouseenter', this.hoverPopupEnter, false);
                this.contentEl.addEventListener('mouseleave', this.hoverLeave, false);
            },
            setupClick() {
                this.clickToggleState = () => {
                    this.setPopperVisible(!this.isPopperVisible);
                };
                // same event is shared by both the trigger element
                // and the popup itself
                this.triggerEl.addEventListener('click', this.clickToggleState, false);
                // auto hides content on click
                if (this.autoHide) {
                    this.contentEl.addEventListener('click', this.clickToggleState, false);
                }
            },
            teardown() {
                // remove events to prevent memory leak
                this.closePopper();
                if (this.isHoverTrigger) {
                    this.triggerEl.removeEventListener('mouseenter', this.hoverTriggerEnter);
                    this.triggerEl.removeEventListener('mouseleave', this.hoverLeave);
                    this.contentEl.removeEventListener('mouseenter', this.hoverPopupEnter);
                    this.contentEl.removeEventListener('mouseleave', this.hoverLeave);
                } else {
                    this.triggerEl.removeEventListener('click', this.clickToggleState);
                    this.contentEl.removeEventListener('click', this.clickToggleState);
                    if (this.autoHide) {
                        this.contentEl.removeEventListener('click', this.clickToggleState);
                    }
                    window.document.removeEventListener('click', this.clickDocumentCheck);
                }
            },
            registerAutoHide() {
                this.clickDocumentCheck = (event) => {
                    // before all else check if we have to verify
                    // if the content is open
                    if (!this.isPopperVisible) {
                        // stop immediately
                        return;
                    }
                    const {target} = event;
                    const isTrigger = target.closest(`[data-popper-id='${this.instanceId}']`) !== null;
                    if (isTrigger) {
                        // allow trigger to function
                        return;
                    }
                    const closest = target.closest('.popper');
                    if (closest === null || !closest.isSameNode(this.$el)) {
                        this.closePopper();
                    }
                };
                window.document.addEventListener('click', this.clickDocumentCheck, false);
            },
            closePopper() {
                this.setPopperVisible(false);
            },
            openPopper() {
                this.setPopperVisible(true);
            },
        },
        mounted() {
            this.mountPopup();
            // couple event for a slot-triggered menu request
            this.$on('request-popper-show', this.openPopper);
            this.$on('request-popper-hide', this.closePopper);
        },
        beforeDestroy() {
            this.teardown();
        },
    };
</script>
